import { useState } from 'react';
import { Search, Filter, X, Calendar } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useCategories } from '@/contexts/CategoriesContext';

interface SearchFilterBarProps {
  onSearch: (query: string) => void;
  onFilterCategory: (categoryId: string | null) => void;
  onFilterType: (type: 'all' | 'gain' | 'expense') => void;
  onFilterDateRange: (range: DateRange | null) => void;
}

interface DateRange {
  start: Date;
  end: Date;
}

export function SearchFilterBar({
  onSearch,
  onFilterCategory,
  onFilterType,
  onFilterDateRange,
}: SearchFilterBarProps) {
  const { t } = useTranslation();
  const { gainCategories, expenseCategories, getCategoryLabel } = useCategories();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<'all' | 'gain' | 'expense'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [quickFilter, setQuickFilter] = useState<string | null>(null);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    onSearch(query);
  };

  const handleTypeChange = (type: 'all' | 'gain' | 'expense') => {
    setSelectedType(type);
    onFilterType(type);
    if (type === 'all') {
      setSelectedCategory(null);
      onFilterCategory(null);
    }
  };

  const handleCategoryChange = (categoryId: string | null) => {
    setSelectedCategory(categoryId);
    onFilterCategory(categoryId);
  };

  const handleQuickFilter = (filter: string) => {
    setQuickFilter(filter === quickFilter ? null : filter);
    
    if (filter === quickFilter) {
      onFilterDateRange(null);
      return;
    }

    const today = new Date();
    let start: Date;
    const end: Date = new Date();

    switch (filter) {
      case 'today':
        start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        break;
      case 'week':
        start = new Date(today);
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start = new Date(today);
        start.setMonth(start.getMonth() - 1);
        break;
      case '3months':
        start = new Date(today);
        start.setMonth(start.getMonth() - 3);
        break;
      case 'year':
        start = new Date(today);
        start.setFullYear(start.getFullYear() - 1);
        break;
      default:
        onFilterDateRange(null);
        return;
    }

    onFilterDateRange({ start, end });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedType('all');
    setSelectedCategory(null);
    setQuickFilter(null);
    onSearch('');
    onFilterType('all');
    onFilterCategory(null);
    onFilterDateRange(null);
  };

  const hasActiveFilters = searchQuery || selectedType !== 'all' || selectedCategory || quickFilter;

  const allCategories = [...gainCategories, ...expenseCategories];
  const categories = selectedType === 'gain' ? gainCategories :
                     selectedType === 'expense' ? expenseCategories :
                     allCategories;

  return (
    <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-4 border border-slate-200 dark:border-slate-700 shadow-xl mb-6">
      {/* Search Bar */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('filter.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-700 border-2 border-transparent focus:border-blue-500 dark:focus:border-purple-500 focus:outline-none transition-all text-slate-900 dark:text-slate-100"
          />
        </div>
        
        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className={`flex items-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all ${
            isFilterOpen || hasActiveFilters
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
          }`}
        >
          <Filter className="w-5 h-5" />
          <span className="hidden sm:inline">{t('filter.filters')}</span>
          {hasActiveFilters && (
            <span className="w-2 h-2 bg-white rounded-full"></span>
          )}
        </button>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-100 dark:bg-red-900/20 text-red-600 hover:bg-red-200 dark:hover:bg-red-900/30 font-semibold transition-all"
            title={t('filter.clear')}
          >
            <X className="w-5 h-5" />
            <span className="hidden sm:inline">{t('common.clear')}</span>
          </button>
        )}
      </div>

      {/* Filter Panel */}
      {isFilterOpen && (
        <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          {/* Transaction Type Filter */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              {t('filter.transactionType')}
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => handleTypeChange('all')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                  selectedType === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                {t('common.all')}
              </button>
              <button
                onClick={() => handleTypeChange('gain')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                  selectedType === 'gain'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                💰 {t('transaction.income')}
              </button>
              <button
                onClick={() => handleTypeChange('expense')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                  selectedType === 'expense'
                    ? 'bg-red-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                💳 {t('transaction.expense')}
              </button>
            </div>
          </div>

          {/* Category Filter */}
          {selectedType !== 'all' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                {t('transaction.category')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleCategoryChange(null)}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    selectedCategory === null
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {t('filter.allCategories')}
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all text-left ${
                      selectedCategory === cat.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick Date Filters */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              {t('filter.quickDateFilters')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[
                { id: 'today', label: t('filter.today') },
                { id: 'week', label: t('filter.last7Days') },
                { id: 'month', label: t('filter.lastMonth') },
                { id: '3months', label: t('filter.last3Months') },
                { id: 'year', label: t('filter.lastYear') },
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => handleQuickFilter(filter.id)}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    quickFilter === filter.id
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('filter.activeFilters')}
          </span>
          {searchQuery && (
            <span className="px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium">
              {t('common.search')}: "{searchQuery}"
            </span>
          )}
          {selectedType !== 'all' && (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              selectedType === 'gain'
                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
            }`}>
              {selectedType === 'gain' ? t('transaction.income') : t('transaction.expense')}
            </span>
          )}
          {selectedCategory && (
            <span className="px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm font-medium">
              {getCategoryLabel(selectedCategory)}
            </span>
          )}
          {quickFilter && (
            <span className="px-3 py-1 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-sm font-medium">
              {quickFilter === 'today' && t('filter.today')}
              {quickFilter === 'week' && t('filter.last7Days')}
              {quickFilter === 'month' && t('filter.lastMonth')}
              {quickFilter === '3months' && t('filter.last3Months')}
              {quickFilter === 'year' && t('filter.lastYear')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}